import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel } from "@/components/ui/panel";
import { useStore } from "@/stores/store-context";
import { CountryRegionPicker } from "@/components/shared/country-region-picker";
import { AvatarField } from "./avatar-field";
import { ChangeEmailModal } from "./change-email-modal";
import { requestErrorSummary } from "@/lib/errors/request-error-summary";
import { profileSaveBody, useReportUnsavedProfileEdits } from "@/lib/profile-draft";

interface ProfileGeneralFormProps {
  /** Whether the form holds edits Save has not written; false again on unmount. The
   *  profile settings modal guards Escape and outside clicks on it (NG-2823). */
  onDirtyChange?: (dirty: boolean) => void;
}

export const ProfileGeneralForm = observer(function ProfileGeneralForm({
  onDirtyChange,
}: ProfileGeneralFormProps) {
  const { t } = useTranslation();
  const { user } = useStore();
  const currentUser = user.currentUser;

  const [firstName, setFirstName] = useState(currentUser?.first_name ?? "");
  const [lastName, setLastName] = useState(currentUser?.last_name ?? "");
  const [displayName, setDisplayName] = useState(currentUser?.display_name ?? "");
  const [mobile, setMobile] = useState(currentUser?.mobile_number ?? "");
  const [holidayRegion, setHolidayRegion] = useState(currentUser?.country_region ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    setFirstName(currentUser.first_name ?? "");
    setLastName(currentUser.last_name ?? "");
    setDisplayName(currentUser.display_name ?? "");
    setMobile(currentUser.mobile_number ?? "");
    setHolidayRegion(currentUser.country_region ?? "");
  }, [
    currentUser,
    currentUser?.id,
    currentUser?.first_name,
    currentUser?.last_name,
    currentUser?.display_name,
    currentUser?.mobile_number,
    currentUser?.country_region,
  ]);

  const draft = { firstName, lastName, displayName, mobile, holidayRegion };
  useReportUnsavedProfileEdits(draft, currentUser, onDirtyChange);

  if (!currentUser) return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await user.updateCurrentUser(profileSaveBody(draft));
      toast.success(t("profile_settings.general.save_success"));
    } catch (error) {
      console.error("[profile] profile save failed", requestErrorSummary(error));
      toast.error(t("profile_settings.general.save_error"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Panel className="space-y-6">
        <AvatarField />

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="profile-first-name">
              {t("profile_settings.general.first_name_label")}
            </Label>
            <Input
              id="profile-first-name"
              type="text"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-last-name">
              {t("profile_settings.general.last_name_label")}
            </Label>
            <Input
              id="profile-last-name"
              type="text"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              disabled={isSaving}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="profile-display-name">
            {t("profile_settings.general.display_name_label")}
          </Label>
          <Input
            id="profile-display-name"
            type="text"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            disabled={isSaving}
          />
          <p className="text-xs text-muted-foreground">
            {t("profile_settings.general.display_name_hint")}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="profile-mobile">{t("profile_settings.general.mobile_label")}</Label>
          <Input
            id="profile-mobile"
            type="tel"
            value={mobile}
            onChange={(event) => setMobile(event.target.value)}
            placeholder={t("profile_settings.general.mobile_placeholder")}
            disabled={isSaving}
          />
        </div>

        <CountryRegionPicker
          idPrefix="profile"
          value={holidayRegion}
          onChange={setHolidayRegion}
          disabled={isSaving}
        />

        <div className="space-y-2">
          <Label htmlFor="profile-email">{t("profile_settings.general.email_label")}</Label>
          <div className="flex items-center gap-2">
            <Input id="profile-email" type="email" value={currentUser.email} disabled readOnly />
            <Button type="button" variant="outline" onClick={() => setEmailModalOpen(true)}>
              {t("profile_settings.general.email_change")}
            </Button>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="button" onClick={() => void handleSave()} disabled={isSaving}>
            {isSaving ? t("profile_settings.general.saving") : t("profile_settings.general.save")}
          </Button>
        </div>
      </Panel>

      <ChangeEmailModal open={emailModalOpen} onOpenChange={setEmailModalOpen} />
    </div>
  );
});
